import React, { useEffect, useMemo, useState } from "react";
import SEO from "@/components/SEO";
import { LangLink as Link } from "@/components/LangLink";
import { useT } from "@/hooks/useT";
import {
  Download as DownloadIcon,
  ShieldCheck,
  Lock,
  BadgeCheck,
  ScanLine,
  ArrowRight,
  Zap,
  WifiOff,
  Bell,
  Sparkles,
  LifeBuoy,
  BookOpen,
  Cpu,
  HardDrive,
  MonitorCheck,
} from "lucide-react";

/* ─────────── OS icon SVGs (inline, official-inspired glyphs) ─────────── */
const WindowsIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M0 3.449 9.75 2.1v9.451H0m10.949-9.6L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-13.051-1.851" />
  </svg>
);

const AppleIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

/* ─────────── OS detection ─────────── */
function detectOS() {
  if (typeof window === "undefined") return null;
  const uaData = navigator.userAgentData;
  const ua = (navigator.userAgent || "").toLowerCase();
  const platform = ((navigator.platform || "") + "").toLowerCase();

  // Try modern userAgentData first
  if (uaData?.platform) {
    const p = uaData.platform.toLowerCase();
    const arch = (uaData.architecture || "").toLowerCase();
    if (p.includes("mac")) {
      // Apple Silicon vs Intel
      if (arch.includes("arm")) return "mac-arm64";
      return "mac-x64";
    }
    if (p.includes("win")) {
      if (arch.includes("arm")) return "win-arm64";
      return "win-x64";
    }
  }

  // Fallback: user-agent sniffing
  if (ua.includes("mac") || platform.includes("mac")) {
    // Apple Silicon browsers still report Intel in UA — best-effort guess
    if (ua.includes("arm") || ua.includes("apple silicon")) return "mac-arm64";
    return "mac-arm64"; // default new Macs are Apple Silicon
  }
  if (ua.includes("win") || platform.includes("win")) {
    if (ua.includes("arm")) return "win-arm64";
    return "win-x64";
  }
  return null;
}

/* ─────────── Download catalog ─────────── */
const BASE = "https://akomoda.co/downloads";
const VERSION = "1.0.0";
const LAST_UPDATED = "15/02/2026";

const DOWNLOADS = [
  {
    id: "win-x64",
    os: "Windows 10 / 11",
    arch: "Intel & AMD (x64)",
    size: "107 MB",
    file: `${BASE}/Akomoda-${VERSION}-win-x64.zip`,
    icon: WindowsIcon,
    accent: "from-[#0078D4] to-[#005A9E]",
    kind: "windows",
  },
  {
    id: "win-arm64",
    os: "Windows ARM",
    arch: "ARM64",
    size: "112 MB",
    file: `${BASE}/Akomoda-${VERSION}-win-arm64.zip`,
    icon: WindowsIcon,
    accent: "from-[#0078D4] to-[#00A4EF]",
    kind: "windows",
  },
  {
    id: "mac-arm64",
    os: "macOS",
    arch: "Apple Silicon",
    subLabel: "M1 · M2 · M3 · M4",
    size: "92 MB",
    file: `${BASE}/Akomoda-${VERSION}-mac-arm64.zip`,
    icon: AppleIcon,
    accent: "from-[#252E3D] to-[#0F172A]",
    kind: "mac",
  },
  {
    id: "mac-x64",
    os: "macOS",
    arch: "Intel",
    size: "96 MB",
    file: `${BASE}/Akomoda-${VERSION}-mac-x64.zip`,
    icon: AppleIcon,
    accent: "from-[#334155] to-[#1E293B]",
    kind: "mac",
  },
];

/* ─────────── GA event helper ─────────── */
function trackDownload(build) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "desktop_download", {
        os: build.kind,           // "windows" | "mac"
        build_id: build.id,       // "win-x64" | "win-arm64" | "mac-arm64" | "mac-x64"
        arch: build.arch,
        version: VERSION,
        file_size: build.size,
        file_url: build.file,
      });
    }
  } catch (_) {
    /* no-op */
  }
}

/* ─────────── Component ─────────── */
export default function Download() {
  const t = useT();
  const [detected, setDetected] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setDetected(detectOS());
    setReady(true);
  }, []);

  const recommended = useMemo(
    () => DOWNLOADS.find((d) => d.id === detected) || null,
    [detected]
  );

  const detectedLabel = useMemo(() => {
    if (!recommended) return "";
    if (recommended.id === "win-x64") return "Windows 10 / 11 · x64";
    if (recommended.id === "win-arm64") return "Windows on ARM64";
    if (recommended.id === "mac-arm64") return "macOS · Apple Silicon";
    if (recommended.id === "mac-x64") return "macOS · Intel";
    return "";
  }, [recommended]);

  return (
    <>
      <SEO
        title={t(
          "Shkarko Akomoda Desktop — Aplikacioni zyrtar",
          "Download Akomoda Desktop — Official App"
        )}
        description={t(
          "Instalo Akomoda Desktop për performancë më të shpejtë, njoftime native, punë offline dhe menaxhim të plotë të hotelit direkt nga kompjuteri.",
          "Install Akomoda Desktop for faster performance, offline capabilities, native notifications and a seamless hotel management experience."
        )}
        canonical="/desktop"
      />

      {/* ═════════════════ HERO ═════════════════ */}
      <section className="relative isolate overflow-hidden bg-[#F8FAFC]" data-testid="download-hero">
        {/* Ambient light */}
        <div
          className="pointer-events-none absolute inset-x-0 -top-40 h-[720px] bg-[radial-gradient(60%_60%_at_50%_0%,rgba(242,99,34,0.14),transparent_70%)]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.25]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(37,46,61,0.14) 1px, transparent 0)",
            backgroundSize: "22px 22px",
            maskImage:
              "linear-gradient(to bottom, black 0%, black 55%, transparent 100%)",
          }}
          aria-hidden
        />

        <div className="container-akm relative pt-16 pb-20 md:pt-24 md:pb-28">
          {/* Small brand chip */}
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#E2E8F0] bg-white/70 px-3.5 py-1.5 text-[11.5px] font-medium text-[#334155] backdrop-blur-md shadow-[0_1px_0_rgba(255,255,255,0.9)_inset]">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#F26322]" />
              {t("Aplikacioni zyrtar Akomoda Desktop", "Official Akomoda Desktop App")}
            </div>
          </div>

          {/* Logo */}
          <div className="mt-8 flex justify-center">
            <img
              src="/akomoda-logo-dark.png"
              alt="Akomoda"
              className="h-10 w-auto md:h-12"
              width="200"
              height="48"
            />
          </div>

          <h1
            className="mx-auto mt-8 max-w-3xl text-center text-[40px] font-semibold leading-[1.05] tracking-[-0.02em] text-[#252E3D] md:text-[56px]"
            data-testid="download-title"
          >
            {t("Shkarko Akomoda Desktop", "Download Akomoda Desktop")}
          </h1>

          <p className="mx-auto mt-6 max-w-2xl text-center text-[17px] leading-relaxed text-[#475569] md:text-[18px]">
            {t(
              "Instalo Akomoda Desktop për performancë më të shpejtë, punë offline, njoftime native dhe një përvojë të pastër menaxhimi hoteli — direkt në kompjuterin tënd.",
              "Install Akomoda Desktop for faster performance, offline capabilities, native notifications and a seamless hotel management experience."
            )}
          </p>

          {/* Feature bullets */}
          <div className="mx-auto mt-8 flex max-w-3xl flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[13px] text-[#475569]">
            <span className="inline-flex items-center gap-2"><Zap size={14} className="text-[#F26322]" /> {t("Më e shpejtë se web", "Faster than the web")}</span>
            <span className="inline-flex items-center gap-2"><WifiOff size={14} className="text-[#F26322]" /> {t("Punon offline", "Works offline")}</span>
            <span className="inline-flex items-center gap-2"><Bell size={14} className="text-[#F26322]" /> {t("Njoftime native", "Native notifications")}</span>
            <span className="inline-flex items-center gap-2"><Sparkles size={14} className="text-[#F26322]" /> {t("Përditësime automatike", "Auto updates")}</span>
          </div>

          {/* ─── Recommended card ─── */}
          <div className="mx-auto mt-12 max-w-3xl" data-testid="download-recommended">
            {ready && recommended ? (
              <RecommendedCard rec={recommended} detectedLabel={detectedLabel} t={t} />
            ) : ready ? (
              <FallbackCard t={t} />
            ) : (
              <div className="h-[168px] animate-pulse rounded-3xl border border-[#E2E8F0] bg-white/70" />
            )}
          </div>
        </div>
      </section>

      {/* ═════════════════ AVAILABLE DOWNLOADS ═════════════════ */}
      <section className="border-t border-[#E2E8F0] bg-white py-20 md:py-24" data-testid="download-cards-section">
        <div className="container-akm">
          <div className="mb-12 flex flex-col items-center text-center">
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#F26322]">
              {t("Të gjitha versionet", "All builds")}
            </span>
            <h2 className="mt-3 text-[28px] font-semibold tracking-[-0.01em] text-[#252E3D] md:text-[36px]">
              {t("Zgjidh sistemin tënd", "Choose your operating system")}
            </h2>
            <p className="mt-3 max-w-xl text-[15px] text-[#64748B]">
              {t(
                "Të gjitha versionet janë të nënshkruara dixhitalisht dhe të skanuara nga viruse.",
                "Every build is digitally signed and virus-scanned."
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {DOWNLOADS.map((d) => (
              <DownloadCard
                key={d.id}
                d={d}
                isRecommended={recommended?.id === d.id}
                t={t}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ═════════════════ SYSTEM REQUIREMENTS ═════════════════ */}
      <section className="border-t border-[#E2E8F0] bg-[#F8FAFC] py-20 md:py-24" data-testid="download-requirements">
        <div className="container-akm grid grid-cols-1 gap-6 md:grid-cols-2">
          <RequirementCard
            icon={WindowsIcon}
            title="Windows"
            items={[
              t("Windows 10 ose më i ri", "Windows 10 or newer"),
              t("Minimumi 4 GB RAM", "4 GB RAM minimum"),
              t("500 MB hapësirë e lirë", "500 MB free storage"),
            ]}
          />
          <RequirementCard
            icon={AppleIcon}
            title="macOS"
            items={[
              t("macOS 12 ose më i ri", "macOS 12 or newer"),
              t("Intel ose Apple Silicon", "Intel or Apple Silicon"),
              t("500 MB hapësirë e lirë", "500 MB free storage"),
            ]}
          />
        </div>
      </section>

      {/* ═════════════════ SECURITY ═════════════════ */}
      <section className="border-t border-[#E2E8F0] bg-white py-20 md:py-24" data-testid="download-security">
        <div className="container-akm">
          <div className="relative overflow-hidden rounded-3xl border border-[#E2E8F0] bg-gradient-to-br from-white to-[#F8FAFC] p-8 shadow-[0_20px_60px_-30px_rgba(37,46,61,0.15)] md:p-12">
            <div
              className="pointer-events-none absolute -top-16 right-[-6%] h-64 w-96 bg-[radial-gradient(closest-side,rgba(242,99,34,0.12),transparent)] blur-3xl"
              aria-hidden
            />
            <div className="flex flex-col items-start gap-3">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#F26322]/25 bg-[#F26322]/8 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#F26322]">
                <ShieldCheck size={13} />
                {t("Siguri e certifikuar", "Certified security")}
              </span>
              <h2 className="max-w-xl text-[26px] font-semibold tracking-[-0.01em] text-[#252E3D] md:text-[32px]">
                {t(
                  "Shkarkim i sigurt, i verifikuar dhe zyrtar.",
                  "A secure, verified and official download."
                )}
              </h2>
              <p className="max-w-2xl text-[15px] text-[#64748B]">
                {t(
                  "Çdo version publikohet nga infrastruktura zyrtare e Akomodës, transmetohet përmes HTTPS dhe kontrollohet për integritet përpara publikimit.",
                  "Every build is published from Akomoda's official infrastructure, delivered over HTTPS, and verified for integrity before release."
                )}
              </p>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
              <TrustPill icon={BadgeCheck} label={t("Publikim zyrtar Akomoda", "Official Akomoda Release")} />
              <TrustPill icon={Lock} label={t("Shkarkim me HTTPS", "Secure HTTPS Download")} />
              <TrustPill icon={ShieldCheck} label={t("Nënshkrim dixhital", "Digitally Signed")} />
              <TrustPill icon={ScanLine} label={t("Skanuar për viruse", "Virus Scanned")} />
            </div>
          </div>
        </div>
      </section>

      {/* ═════════════════ HELP ═════════════════ */}
      <section className="border-t border-[#E2E8F0] bg-[#252E3D] py-20 md:py-24" data-testid="download-help">
        <div className="container-akm text-center">
          <div className="mx-auto max-w-2xl">
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#F26322]">
              {t("Mbështetja jonë", "Our support")}
            </span>
            <h2 className="mt-4 text-[28px] font-semibold tracking-[-0.01em] text-white md:text-[36px]">
              {t("Të duhet ndihmë me instalimin?", "Need help installing Akomoda?")}
            </h2>
            <p className="mt-4 text-[15.5px] leading-relaxed text-[#94A3B8]">
              {t(
                "Ekipi ynë i mbështetjes është këtu për të të ndihmuar me instalimin, përditësimet dhe çdo problem teknik.",
                "Our support team is here to help you with installation, updates and any technical issue."
              )}
            </p>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 rounded-full bg-[#F26322] px-6 py-3 text-[14px] font-semibold text-white shadow-[0_10px_30px_-10px_rgba(242,99,34,0.6)] transition-all hover:-translate-y-0.5 hover:bg-[#E5570F]"
                data-testid="download-help-support"
              >
                <LifeBuoy size={16} />
                {t("Mbështetje", "Support")}
                <ArrowRight size={14} />
              </Link>
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-[14px] font-semibold text-white backdrop-blur-md transition-all hover:-translate-y-0.5 hover:bg-white/10"
                data-testid="download-help-docs"
              >
                <BookOpen size={16} />
                {t("Dokumentacioni", "Documentation")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═════════════════ FOOTER STRIP ═════════════════ */}
      <section className="border-t border-[#E2E8F0] bg-white py-8" data-testid="download-meta">
        <div className="container-akm flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[13px] text-[#64748B]">
          <span className="inline-flex items-center gap-1.5">
            <span className="rounded-md bg-[#F1F5F9] px-2 py-0.5 font-mono text-[11.5px] font-medium text-[#334155]">v{VERSION}</span>
          </span>
          <span>
            {t("Përditësuar më", "Last updated")}: <span className="font-medium text-[#334155]">{LAST_UPDATED}</span>
          </span>
        </div>
      </section>
    </>
  );
}

/* ─────────── Sub-components ─────────── */

function RecommendedCard({ rec, detectedLabel, t }) {
  const Icon = rec.icon;
  return (
    <div
      className="group relative overflow-hidden rounded-3xl border border-[#E2E8F0] bg-white/70 p-6 shadow-[0_30px_80px_-40px_rgba(37,46,61,0.25)] backdrop-blur-xl md:p-8"
      data-testid="download-recommended-card"
    >
      {/* Orange accent gradient */}
      <div
        className="pointer-events-none absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-[#F26322]/70 to-transparent"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[radial-gradient(closest-side,rgba(242,99,34,0.22),transparent)] blur-2xl"
        aria-hidden
      />

      <div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-5">
          <div className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-[#252E3D] to-[#0F172A] text-white shadow-inner">
            <Icon className="h-8 w-8" />
          </div>
          <div>
            <div className="inline-flex items-center gap-1.5 rounded-full bg-[#F26322]/10 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.18em] text-[#F26322]">
              <MonitorCheck size={12} />
              {t("Rekomanduar për pajisjen tënde", "Recommended for your device")}
            </div>
            <div className="mt-2 text-[13px] text-[#64748B]">{t("Zbuluar", "Detected")}</div>
            <div className="text-[20px] font-semibold tracking-[-0.01em] text-[#252E3D] md:text-[22px]" data-testid="download-detected-label">
              {detectedLabel}
            </div>
            <div className="mt-1 text-[13px] text-[#64748B]">
              Version {VERSION} · {rec.size}
            </div>
          </div>
        </div>

        <a
          href={rec.file}
          onClick={() => trackDownload({ ...rec, source: "recommended" })}
          className="group/btn inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#F26322] px-7 py-4 text-[15px] font-semibold text-white shadow-[0_18px_40px_-15px_rgba(242,99,34,0.7)] transition-all hover:-translate-y-0.5 hover:bg-[#E5570F] md:w-auto md:min-w-[220px]"
          data-testid="download-recommended-cta"
        >
          <DownloadIcon size={17} />
          {t("Shkarko për", "Download for")}{" "}
          {rec.kind === "windows" ? "Windows" : "macOS"}
          <ArrowRight size={15} className="transition-transform group-hover/btn:translate-x-0.5" />
        </a>
      </div>
    </div>
  );
}

function FallbackCard({ t }) {
  return (
    <div className="rounded-3xl border border-[#E2E8F0] bg-white/70 p-6 text-center backdrop-blur-xl md:p-8" data-testid="download-fallback">
      <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-[#F26322]/10 text-[#F26322]">
        <Cpu size={22} />
      </div>
      <h3 className="mt-4 text-[18px] font-semibold text-[#252E3D]">
        {t("Nuk mundëm ta zbulojmë pajisjen tënde", "We couldn't detect your device")}
      </h3>
      <p className="mt-2 text-[14px] text-[#64748B]">
        {t(
          "Zgjidh sistemin operativ nga lista më poshtë.",
          "Choose your operating system from the list below."
        )}
      </p>
    </div>
  );
}

function DownloadCard({ d, isRecommended, t }) {
  const Icon = d.icon;
  return (
    <div
      className={`group relative flex flex-col overflow-hidden rounded-3xl border bg-white p-6 transition-all duration-300 md:p-7 ${
        isRecommended
          ? "border-[#F26322]/40 shadow-[0_25px_70px_-30px_rgba(242,99,34,0.35)] ring-1 ring-[#F26322]/20"
          : "border-[#E2E8F0] shadow-[0_8px_24px_-16px_rgba(37,46,61,0.15)] hover:-translate-y-1 hover:border-[#CBD5E1] hover:shadow-[0_20px_50px_-25px_rgba(37,46,61,0.25)]"
      }`}
      data-testid={`download-card-${d.id}`}
    >
      {isRecommended && (
        <div className="absolute right-5 top-5 inline-flex items-center gap-1 rounded-full bg-[#F26322] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white">
          <Sparkles size={11} />
          {t("Rekomanduar", "Recommended")}
        </div>
      )}

      <div className="flex items-start gap-4">
        <div
          className={`grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-gradient-to-br ${d.accent} text-white`}
        >
          <Icon className="h-7 w-7" />
        </div>
        <div className="min-w-0">
          <h3 className="truncate text-[19px] font-semibold tracking-[-0.005em] text-[#252E3D]">
            {d.os}
          </h3>
          <p className="mt-0.5 text-[13.5px] text-[#64748B]">{d.arch}</p>
          {d.subLabel && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {d.subLabel.split("·").map((chip) => (
                <span
                  key={chip}
                  className="rounded-md bg-[#F1F5F9] px-2 py-0.5 text-[11px] font-medium text-[#334155]"
                >
                  {chip.trim()}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 border-t border-[#F1F5F9] pt-5">
        <div>
          <div className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-[#94A3B8]">
            {t("Versioni", "Version")}
          </div>
          <div className="mt-1 text-[14px] font-semibold text-[#252E3D]">
            {VERSION}
          </div>
        </div>
        <div>
          <div className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-[#94A3B8]">
            {t("Madhësia", "File size")}
          </div>
          <div className="mt-1 inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#252E3D]">
            <HardDrive size={13} className="text-[#94A3B8]" />
            {d.size}
          </div>
        </div>
      </div>

      <a
        href={d.file}
        onClick={() => trackDownload(d)}
        className={`mt-6 inline-flex items-center justify-center gap-2 rounded-2xl px-5 font-semibold transition-all ${
          isRecommended
            ? "bg-[#F26322] py-3.5 text-[15px] text-white shadow-[0_12px_30px_-12px_rgba(242,99,34,0.65)] hover:-translate-y-0.5 hover:bg-[#E5570F]"
            : "border border-[#E2E8F0] bg-white py-3 text-[14px] text-[#252E3D] hover:border-[#F26322]/40 hover:bg-[#FFF8F3] hover:text-[#F26322]"
        }`}
        data-testid={`download-btn-${d.id}`}
      >
        <DownloadIcon size={15} />
        {t("Shkarko", "Download")}
      </a>
    </div>
  );
}

function RequirementCard({ icon: Icon, title, items }) {
  return (
    <div className="rounded-3xl border border-[#E2E8F0] bg-white p-8 shadow-[0_10px_30px_-20px_rgba(37,46,61,0.15)]">
      <div className="flex items-center gap-3">
        <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#252E3D] text-white">
          <Icon className="h-5 w-5" />
        </div>
        <h3 className="text-[18px] font-semibold tracking-[-0.005em] text-[#252E3D]">
          {title}
        </h3>
      </div>
      <ul className="mt-6 space-y-3">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-3 text-[14.5px] text-[#334155]">
            <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F26322]/12 text-[#F26322]">
              <BadgeCheck size={13} />
            </span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function TrustPill({ icon: Icon, label }) {
  return (
    <div className="group flex items-center gap-3 rounded-2xl border border-[#E2E8F0] bg-white/80 p-4 backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-[#F26322]/30 hover:bg-white">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#F26322]/10 text-[#F26322] transition-colors group-hover:bg-[#F26322] group-hover:text-white">
        <Icon size={17} />
      </span>
      <span className="text-[13.5px] font-medium leading-tight text-[#252E3D]">
        {label}
      </span>
    </div>
  );
}
